import type {
  BedrockCatalogModel,
  OrgLlmConnectionKind,
  OrgLlmConnectionStatus,
} from "./OrgLlmProviderRecord";

/** Display name of each kind of own LLM connection. */
export const ORG_LLM_CONNECTION_KIND_LABELS: Record<
  OrgLlmConnectionKind,
  string
> = {
  anthropic_api: "Anthropic API",
  openai_compat: "OpenAI-compatible",
};

/** Display name of each connection status. */
export const ORG_LLM_CONNECTION_STATUS_LABELS: Record<
  OrgLlmConnectionStatus,
  string
> = {
  active: "Active",
  failing: "Failing",
  disabled: "Disabled",
};

/** The fields of a list entry that decide where it runs and whether two lists are the same offer. */
interface ModelChoice {
  id: string;
  is_default: boolean;
  connection_id?: string | null;
}

/** Whether a list entry is a Roboto-managed Bedrock model (billed through AI credits). */
export function isRobotoManaged(model: {
  connection_id?: string | null;
}): boolean {
  return model.connection_id === null || model.connection_id === undefined;
}

/**
 * Whether `models` is exactly the Roboto default offer: the same
 * Roboto-managed profile ids (in any order) with the same one marked default,
 * and nothing else.
 */
export function isRobotoDefaultModels(
  models: readonly ModelChoice[],
  robotoDefault: readonly ModelChoice[],
): boolean {
  if (
    models.length !== robotoDefault.length ||
    !models.every(isRobotoManaged)
  ) {
    return false;
  }
  const defaultIds = new Map(robotoDefault.map((m) => [m.id, m.is_default]));
  return models.every(
    (m) => defaultIds.has(m.id) && defaultIds.get(m.id) === m.is_default,
  );
}

/**
 * One line naming where an org's chat models come from:
 *
 * - "Roboto default" — exactly the Roboto default models.
 * - "Roboto models (custom)" — Roboto-managed models, another selection.
 * - "Roboto models" — Roboto-managed models, when the Roboto default is not known.
 * - "Roboto + own models" — both Roboto-managed and own-connection models.
 * - "Own models" — only models on the org's own connections.
 *
 * @param robotoDefault The Roboto default models, or `undefined` when they
 *   have not loaded (or cannot be known to this caller).
 */
export function describeModelSource(
  models: readonly ModelChoice[],
  robotoDefault: readonly ModelChoice[] | undefined,
): string {
  const own = models.filter((m) => !isRobotoManaged(m)).length;
  if (own === models.length && own > 0) {
    return "Own models";
  }
  if (own > 0) {
    return "Roboto + own models";
  }
  if (robotoDefault === undefined) {
    return "Roboto models";
  }
  return isRobotoDefaultModels(models, robotoDefault)
    ? "Roboto default"
    : "Roboto models (custom)";
}

/**
 * What a chat model that `needs_price` is missing, as an instruction ("Set
 * the AWS cost first."), or `undefined` when it needs nothing. A chat entry
 * needs both its AWS cost and its customer price.
 */
export function missingPriceBlocker(
  model: Pick<BedrockCatalogModel, "needs_price" | "cost" | "customer_price">,
): string | undefined {
  if (!model.needs_price) {
    return undefined;
  }
  const noCost = model.cost === null;
  const noPrice = model.customer_price === null;
  if (noCost && noPrice) {
    return "Set the AWS cost and customer price first.";
  }
  return noCost ? "Set the AWS cost first." : "Set the customer price first.";
}

/**
 * Why a chat model cannot be offered to orgs (or new orgs) yet, or
 * `undefined` when it can: it lacks its AWS cost or customer price, it has
 * not been verified, or it cannot use tools, which every chat turn carries.
 */
export function offerBlocker(model: BedrockCatalogModel): string | undefined {
  const missingPrice = missingPriceBlocker(model);
  if (missingPrice !== undefined) {
    return missingPrice;
  }
  if (!model.verified) {
    return "Verify first.";
  }
  if (!model.capabilities.tool_use) {
    return "Tool use is off, so it can't serve a chat turn.";
  }
  return undefined;
}
